import { AudioCue } from '../types/audio';

/**
 * Procedural SFX and Subharmonic Engine Generator.
 * Synthesizes:
 * 1. Tactile page-turn micro-clicks via FM transient impulse.
 * 2. 18–24Hz subharmonic engine pulse modulated by a low-frequency oscillator (LFO).
 */
export class SFXGen {
  private ctx: AudioContext;
  private outputNode: GainNode;
  private subharmonicGainNode: GainNode;
  private subharmonicFilter: BiquadFilterNode;

  // Subharmonic pulse oscillator & LFO
  private subOsc: OscillatorNode | null = null;
  private lfoOsc: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;

  private isStarted = false;
  private currentSubFreq = 20.0; // 18-24Hz range

  constructor(ctx: AudioContext) {
    this.ctx = ctx;

    // Output node for all SFX and subharmonics
    this.outputNode = this.ctx.createGain();
    this.outputNode.gain.setValueAtTime(1.0, this.ctx.currentTime);

    // Deep sub-bass filter (isolates fundamental sub frequencies)
    this.subharmonicFilter = this.ctx.createBiquadFilter();
    this.subharmonicFilter.type = 'lowpass';
    this.subharmonicFilter.frequency.setValueAtTime(75, this.ctx.currentTime);
    this.subharmonicFilter.Q.setValueAtTime(1.5, this.ctx.currentTime);

    // Subharmonic gain control
    this.subharmonicGainNode = this.ctx.createGain();
    this.subharmonicGainNode.gain.setValueAtTime(0.35, this.ctx.currentTime);

    this.subharmonicGainNode.connect(this.subharmonicFilter);
    this.subharmonicFilter.connect(this.outputNode);
  }

  public connect(destination: AudioNode): void {
    this.outputNode.connect(destination);
  }

  public getOutputNode(): GainNode {
    return this.outputNode;
  }

  public getSubharmonicFilter(): BiquadFilterNode {
    return this.subharmonicFilter;
  }

  public getSubharmonicGainNode(): GainNode {
    return this.subharmonicGainNode;
  }

  public getSubharmonicFrequency(): number {
    return this.currentSubFreq;
  }

  public getSubOscillator(): OscillatorNode | null {
    return this.subOsc;
  }

  public getLfoOscillator(): OscillatorNode | null {
    return this.lfoOsc;
  }

  public start(): void {
    if (this.isStarted) return;
    this.isStarted = true;

    const t = this.ctx.currentTime;

    // Subharmonic oscillator (18Hz - 24Hz fundamental)
    this.subOsc = this.ctx.createOscillator();
    this.subOsc.type = 'sine';
    this.subOsc.frequency.setValueAtTime(this.currentSubFreq, t);

    // LFO (Low-Frequency Oscillator) for rhythmic engine pulse
    this.lfoOsc = this.ctx.createOscillator();
    this.lfoOsc.type = 'sine';
    this.lfoOsc.frequency.setValueAtTime(0.08, t); // ~12s slow swell cycle

    this.lfoGain = this.ctx.createGain();
    this.lfoGain.gain.setValueAtTime(0.18, t);

    // Route LFO to modulate subharmonic gain
    this.lfoOsc.connect(this.lfoGain);
    this.lfoGain.connect(this.subharmonicGainNode.gain);

    this.subOsc.connect(this.subharmonicGainNode);

    this.subOsc.start(t);
    this.lfoOsc.start(t);
  }

  public stop(): void {
    if (!this.isStarted) return;
    this.isStarted = false;

    const t = this.ctx.currentTime;
    try {
      this.subOsc?.stop(t);
      this.lfoOsc?.stop(t);
      this.subOsc?.disconnect();
      this.lfoOsc?.disconnect();
      this.lfoGain?.disconnect();
    } catch {
      // Ignore
    }

    this.subOsc = null;
    this.lfoOsc = null;
    this.lfoGain = null;
  }

  /**
   * Generates a tactile procedural page-turn micro-click.
   * Employs dual FM transient synthesis without external audio files.
   */
  public triggerPageTurnSound(volume = 0.12): void {
    const t = this.ctx.currentTime;

    try {
      // Primary click transient (Carrier + Modulator)
      const carrier = this.ctx.createOscillator();
      const modulator = this.ctx.createOscillator();
      const modGain = this.ctx.createGain();
      const clickGain = this.ctx.createGain();

      carrier.type = 'sine';
      carrier.frequency.setValueAtTime(880, t);
      carrier.frequency.exponentialRampToValueAtTime(140, t + 0.035);

      modulator.type = 'triangle';
      modulator.frequency.setValueAtTime(110, t);

      modGain.gain.setValueAtTime(320, t);
      modGain.gain.exponentialRampToValueAtTime(1, t + 0.035);

      modulator.connect(carrier.frequency);

      // Amplitude decay envelope
      clickGain.gain.setValueAtTime(volume, t);
      clickGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.038);

      carrier.connect(clickGain);
      clickGain.connect(this.outputNode);

      carrier.start(t);
      modulator.start(t);
      carrier.stop(t + 0.04);
      modulator.stop(t + 0.04);

      // Secondary micro-pulse (quantum relay tick at +12ms)
      const tick = this.ctx.createOscillator();
      const tickGain = this.ctx.createGain();
      tick.type = 'sine';
      tick.frequency.setValueAtTime(1640, t + 0.012);
      tick.frequency.exponentialRampToValueAtTime(420, t + 0.026);

      tickGain.gain.setValueAtTime(0.0001, t);
      tickGain.gain.setValueAtTime(volume * 0.45, t + 0.012);
      tickGain.gain.exponentialRampToValueAtTime(0.0001, t + 0.028);

      tick.connect(tickGain);
      tickGain.connect(this.outputNode);

      tick.start(t + 0.012);
      tick.stop(t + 0.03);
    } catch {
      // AudioContext might be closing or unready
    }
  }

  /**
   * Smoothly crossfades subharmonic engine pulse across moods.
   */
  public updateCue(cue: AudioCue, duration = 2.0): void {
    if (!this.isStarted) {
      this.start();
    }

    const t = this.ctx.currentTime;
    const endTime = t + duration;

    // Subharmonic frequency strictly clamped within 18Hz–24Hz range
    const targetSubFreq = 18.0 + Math.max(0, Math.min(1.0, cue.subharmonicBass)) * 6.0;
    this.currentSubFreq = targetSubFreq;

    let targetLfoRate = 0.08;
    let targetLfoDepth = 0.15;
    let targetSubGain = 0.35 * cue.subharmonicBass;

    switch (cue.mood) {
      case 'deep_drone':
        targetLfoRate = 0.06; // Slow deep breath (16s cycle)
        targetLfoDepth = 0.12;
        targetSubGain = 0.4 * cue.subharmonicBass;
        break;

      case 'quantum_flutter':
        targetLfoRate = 0.4 + cue.glitchFrequency * 1.2; // Fast flutter
        targetLfoDepth = 0.25;
        targetSubGain = 0.3 * cue.subharmonicBass;
        break;

      case 'subharmonic_pulse':
        // Heavy throbbing engine
        targetLfoRate = 0.15;
        targetLfoDepth = 0.28;
        targetSubGain = 0.65 * Math.max(cue.subharmonicBass, 0.4);
        break;

      case 'caesura_silence':
        // Dead silence
        targetSubGain = 0.0;
        targetLfoDepth = 0.0;
        break;

      case 'synaptic_alarm':
        targetLfoRate = 0.8 + cue.glitchFrequency * 2.0;
        targetLfoDepth = 0.32;
        targetSubGain = 0.45 * cue.subharmonicBass;
        break;
    }

    // Ramp subharmonic frequency
    if (this.subOsc) {
      this.subOsc.frequency.cancelScheduledValues(t);
      this.subOsc.frequency.setValueAtTime(this.subOsc.frequency.value, t);
      this.subOsc.frequency.linearRampToValueAtTime(targetSubFreq, endTime);
    }

    // Ramp LFO rate
    if (this.lfoOsc) {
      this.lfoOsc.frequency.cancelScheduledValues(t);
      this.lfoOsc.frequency.setValueAtTime(this.lfoOsc.frequency.value, t);
      this.lfoOsc.frequency.linearRampToValueAtTime(targetLfoRate, endTime);
    }

    // Ramp LFO depth
    if (this.lfoGain) {
      this.lfoGain.gain.cancelScheduledValues(t);
      this.lfoGain.gain.setValueAtTime(this.lfoGain.gain.value, t);
      this.lfoGain.gain.linearRampToValueAtTime(targetLfoDepth, endTime);
    }

    // Ramp subharmonic base gain
    this.subharmonicGainNode.gain.cancelScheduledValues(t);
    this.subharmonicGainNode.gain.setValueAtTime(this.subharmonicGainNode.gain.value, t);
    this.subharmonicGainNode.gain.linearRampToValueAtTime(Math.max(0, targetSubGain), endTime);
  }
}
